import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AutocompleteInput, SimpleForm } from 'react-admin';
import {
  Typography,
  Button,
  Box,
  IconButton,
  Dialog,
  LinearProgress,
  TextField,
  MenuItem,
} from '@material-ui/core';
import { Adjust, AddCircle, Close, Delete, Save } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useFormState } from 'react-final-form';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { closeTaskStyles } from '../../constants';
import { asyncFetch } from '../../helperFunctions';
import { setSnackbar } from '../redux';

const styles = {
  widthHack: { display: 'flex' },
  typographyAsContainer: { display: 'flex', alignItems: 'center', gap: '10px' },
  popupCloseButton: { position: 'absolute', top: 0, right: 0, zIndex: 100 },
  inputContainer: { display: 'flex', justifyContent: 'center', flexDirection: 'column' },
  responseContainer: { padding: 10 },
  receiversContainer: { display: 'flex', flexDirection: 'column', marginTop: 16 },
  receiversLabel: { color: 'rgba(0, 0, 0, 0.54)' },
  receiverRow: { display: 'flex', alignItems: 'flex-end', gap: '10px', marginTop: 8 },
  receiverRowLabel: { minWidth: 45 },
  addReceiverButton: { alignSelf: 'flex-start', marginTop: 12 },
};

// Component id per receiver type and receiver position (1..4)
const ADDITIONAL_RECEIVER_TYPES = {
  iptv: { name: 'IPTV', componentIds: { 1: 55212, 2: 55213, 3: 55214, 4: 55215 } },
  dtv: { name: 'DTV', componentIds: { 1: 55400, 2: 55401, 3: 55402, 4: 55403 } },
  xploreTv: { name: 'Xplore TV', componentIds: { 1: 55710, 2: 55711, 3: 55712, 4: 55713 } },
  smartTv: {
    name: 'Smart TV приложение',
    componentIds: { 1: 55816, 2: 55817, 3: 55818, 4: 55819 },
  },
  androidStb: { name: 'Android STB', componentIds: { 1: 55892, 2: 55893, 3: 55894, 4: 55895 } },
};

// Always pickable, whatever the TV technology is. The selected TV technology adds one more
// option on top of these (see receiverTypeForTvTechnology).
const SELECTABLE_RECEIVER_TYPES = ['smartTv', 'androidStb'];

// The rest of the types belong to a single TV technology, so a receiver holding one of them has
// to follow the TV technology selected in the form.
const TV_TECHNOLOGY_RECEIVER_TYPES = Object.keys(ADDITIONAL_RECEIVER_TYPES).filter(
  type => !SELECTABLE_RECEIVER_TYPES.includes(type)
);

const MAX_ADDITIONAL_RECEIVERS = 4;

// tvTechnology choice id -> receiver type of that same technology. 4 (Xplore TV) is the only
// id confirmed so far; anything missing here falls back to matching the choice label below.
const RECEIVER_TYPE_BY_TV_TECHNOLOGY_ID = {
  4: 'xploreTv',
};

// Backend labels for a TV technology, normalised, mapped to the matching receiver type.
const RECEIVER_TYPE_BY_TV_TECHNOLOGY_NAME = {
  iptv: 'iptv',
  dtv: 'dtv',
  xploretv: 'xploreTv',
};

const normalizeTechnologyName = name => (name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// The receiver type matching the currently selected TV technology, or undefined when that
// technology has no receiver of its own (e.g. Smart TV).
const receiverTypeForTvTechnology = (tvTechnology, tvTechnologyChoices) => {
  const byId = RECEIVER_TYPE_BY_TV_TECHNOLOGY_ID[tvTechnology];

  if (byId) {
    return byId;
  }

  const choice = (tvTechnologyChoices ?? []).find(x => x.id === tvTechnology);

  return RECEIVER_TYPE_BY_TV_TECHNOLOGY_NAME[normalizeTechnologyName(choice?.name)];
};

const ADDITIONAL_RECEIVERS_BY_COMPONENT_ID = Object.entries(ADDITIONAL_RECEIVER_TYPES).reduce(
  (acc, [type, { componentIds }]) => {
    Object.entries(componentIds).forEach(([position, componentId]) => {
      acc[componentId] = { type, position: Number(position) };
    });
    return acc;
  },
  {}
);

// The receiver of the selected TV technology comes first, then the always-pickable types, then
// whatever the row currently holds — so a receiver coming from the API is never an empty select.
const receiverTypeChoices = (type, tvReceiverType) => {
  const types = [];

  [tvReceiverType, ...SELECTABLE_RECEIVER_TYPES, type].forEach(x => {
    if (x && !types.includes(x)) {
      types.push(x);
    }
  });

  return types.map(id => ({ id, name: ADDITIONAL_RECEIVER_TYPES[id].name }));
};

// The form value stays an array of component ids (that is what saveTechnology expects);
// the rows shown in the popup are derived from it, ordered by receiver position.
const receiverRowsFromComponentIds = componentIds =>
  (componentIds ?? [])
    .map(id => ADDITIONAL_RECEIVERS_BY_COMPONENT_ID[id])
    .filter(Boolean)
    .sort((a, b) => a.position - b.position)
    .map(x => x.type);

const componentIdsFromReceiverRows = rows =>
  rows.map((type, index) => ADDITIONAL_RECEIVER_TYPES[type].componentIds[index + 1]);

// Receivers of another TV technology are switched over to the selected one; Smart TV and Android
// STB are applicable for every technology, so they are kept as they are. When the selected
// technology has no receiver of its own, the always-pickable type takes over.
const receiverRowsForTvTechnology = (rows, tvReceiverType) =>
  rows.map(type =>
    TV_TECHNOLOGY_RECEIVER_TYPES.includes(type)
      ? tvReceiverType ?? SELECTABLE_RECEIVER_TYPES[0]
      : type
  );

const AmendTvInput = ({ amendData }) => {
  const { change } = useForm();

  const handleTvChange = tvTechnology => {
    if (tvTechnology === 1) {
      change('internetTechnology', 1);
    }
    if (tvTechnology === 2) {
      change('internetTechnology', 2);
    }
  };

  return (
    amendData.hasTv && (
      <AutocompleteInput
        variant="standard"
        choices={amendData.tvTechnology}
        source="tvTechnology"
        label="Тв технология"
        onChange={x => handleTvChange(x)}
      />
    )
  );
};

const AmendInternetInput = ({ amendData }) => {
  const { values } = useFormState();

  return (
    amendData.hasNet && (
      <AutocompleteInput
        disabled={[1, 2].includes(values.tvTechnology)}
        variant="standard"
        choices={amendData.internetTechnology}
        source="internetTechnology"
        label="Интернет технология"
      />
    )
  );
};

const AmendAdditionalReceiversInput = ({ amendData }) => {
  const { fontSize } = useWindowDimensions();
  const { change } = useForm();
  const { values } = useFormState();
  const { additionalReceivers, tvTechnology } = values;

  const rows = useMemo(() => receiverRowsFromComponentIds(additionalReceivers), [
    additionalReceivers,
  ]);

  const tvReceiverType = receiverTypeForTvTechnology(tvTechnology, amendData.tvTechnology);

  const updateRows = useCallback(
    newRows => {
      // Ids the popup does not render (e.g. the auto-added OTT ones) must survive an edit.
      const untouched = (additionalReceivers ?? []).filter(
        id => !ADDITIONAL_RECEIVERS_BY_COMPONENT_ID[id]
      );

      change('additionalReceivers', [...untouched, ...componentIdsFromReceiverRows(newRows)]);
    },
    [additionalReceivers, change]
  );

  // Keep the receivers in sync with the selected TV technology.
  useEffect(() => {
    const newRows = receiverRowsForTvTechnology(rows, tvReceiverType);

    if (newRows.some((type, index) => type !== rows[index])) {
      updateRows(newRows);
    }
  }, [rows, tvReceiverType, updateRows]);

  return (
    <Box style={styles.receiversContainer}>
      <Typography style={{ ...fontSize, ...styles.receiversLabel }}>Доп приемници</Typography>

      {rows.map((type, index) => (
        <Box
          key={ADDITIONAL_RECEIVER_TYPES[type].componentIds[index + 1]}
          style={styles.receiverRow}
        >
          <Typography style={{ ...fontSize, ...styles.receiverRowLabel }}>
            {`Stb ${index + 1}`}
          </Typography>
          <TextField
            select
            fullWidth
            variant="standard"
            value={type}
            onChange={x =>
              updateRows(rows.map((row, i) => (i === index ? x.target.value : row)))
            }
          >
            {receiverTypeChoices(type, tvReceiverType).map(choice => (
              <MenuItem key={choice.id} value={choice.id}>
                {choice.name}
              </MenuItem>
            ))}
          </TextField>
          <IconButton onClick={() => updateRows(rows.filter((row, i) => i !== index))}>
            <Delete />
          </IconButton>
        </Box>
      ))}

      {rows.length < MAX_ADDITIONAL_RECEIVERS && (
        <Button
          style={styles.addReceiverButton}
          onClick={() =>
            updateRows([...rows, receiverTypeChoices(undefined, tvReceiverType)[0].id])
          }
          variant="contained"
          color="secondary"
        >
          <Typography component="span" style={{ ...fontSize, ...styles.typographyAsContainer }}>
            <AddCircle />
            Добави STB
          </Typography>
        </Button>
      )}
    </Box>
  );
};

const AmendForm = ({ ticketId, setOpenAmendForm }) => {
  const [amendData, setAmendData] = useState();
  const dispatch = useDispatch();
  const loginInfo = useSelector(state => state.auth);
  const { user } = loginInfo;

  useEffect(() => {
    if (!amendData) {
      (async () => {
        const response = await asyncFetch(
          `${process.env.REACT_APP_AMEND_API}/amend/getAmendData?ticketId=${ticketId}`
        );

        if (response?.error === null) {
          setAmendData(response);
        } else {
          response?.error &&
            dispatch(
              setSnackbar({
                open: true,
                message: `${response.error}`,
              })
            );
          setOpenAmendForm(false);
        }
      })();
    }
  }, [amendData, ticketId, setOpenAmendForm, dispatch]);

  return amendData ? (
    <SimpleForm
      toolbar={<AmendFormHelper amendData={amendData} userId={user.userId} ticketId={ticketId} />}
    >
      <Box style={styles.inputContainer}>
        <IconButton style={styles.popupCloseButton} onClick={() => setOpenAmendForm(false)}>
          <Close />
        </IconButton>
        <AmendTvInput amendData={amendData} />
        <AmendInternetInput amendData={amendData} />
        {amendData.hasTv && <AmendAdditionalReceiversInput amendData={amendData} />}
      </Box>
    </SimpleForm>
  ) : (
    <></>
  );
};

const AmendFormHelper = ({ amendData, userId, ticketId }) => {
  const { fontSize } = useWindowDimensions();
  const { change, batch } = useForm();
  const { values } = useFormState();
  const { tvTechnology, smartTvDeviceType, internetTechnology, additionalReceivers } = values;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState();
  // The amend ended, successfully or with an error — it must not be executed again from here,
  // the technician has to reopen the popup and start the process from the beginning.
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    batch(() => {
      amendData.defaultTvTechnology && change('tvTechnology', amendData.defaultTvTechnology);
      amendData.defaultSmartTvDeviceType &&
        change('smartTvDeviceType', amendData.defaultSmartTvDeviceType);
      amendData.defaultInternetTechnology &&
        change('internetTechnology', amendData.defaultInternetTechnology);
      amendData.defaultAdditionalReceivers &&
        change('additionalReceivers', amendData.defaultAdditionalReceivers);
    });
  }, [change, batch, amendData]);

  const checkAmend = amendId => {
    (async () => {
      const response = await asyncFetch(
        `${process.env.REACT_APP_AMEND_API}/amend/checkAmend?amendId=${amendId}`,
        {
          method: 'GET',
          mode: 'cors',
          referrerPolicy: 'strict-origin-when-cross-origin',
        },
        {
          'Content-Type': 'application/json',
        },
        undefined,
        dispatch
      );

      if (response) {
        if (response.isCompleted) {
          setResponseMessage(response.message);
          setLoading(false);
          setFinished(true);
        } else {
          setTimeout(() => {
            checkAmend(amendId);
          }, 10000);
        }
      }
    })();
  };

  const saveTechnology = () => {
    setLoading(true);
    (async () => {
      const response = await asyncFetch(
        `${process.env.REACT_APP_AMEND_API}/amend/saveTechnology`,
        {
          method: 'POST',
          mode: 'cors',
          referrerPolicy: 'strict-origin-when-cross-origin',
          body: JSON.stringify({
            userId,
            ticketId,
            tvTechnology,
            smartTvDeviceType,
            internetTechnology,
            additionalReceivers,
          }),
        },
        {
          'Content-Type': 'application/json',
        },
        undefined,
        dispatch
      );

      if (response) {
        if (response.error) {
          setResponseMessage(response.error);
          setLoading(false);
          setFinished(true);
        } else {
          setTimeout(() => {
            checkAmend(response.amendId);
          }, 10000);
        }
      }
    })();
  };

  return (
    <>
      <Box style={styles.responseContainer}>
        {responseMessage && <Typography>{responseMessage}</Typography>}
        {loading && <LinearProgress />}
      </Box>

      {!finished && (
        <Button
          disabled={loading}
          style={closeTaskStyles.margin}
          onClick={saveTechnology}
          variant="contained"
          color="secondary"
        >
          <Typography component="span" style={{ ...fontSize, ...styles.typographyAsContainer }}>
            <Save />
            Запази
          </Typography>
        </Button>
      )}
    </>
  );
};

const Amend = ({ ticketId }) => {
  const { fontSize } = useWindowDimensions();
  const [openAmendForm, setOpenAmendForm] = useState(false);

  return (
    <>
      <Box style={styles.widthHack}>
        <Button
          style={closeTaskStyles.margin}
          onClick={() => setOpenAmendForm(true)}
          variant="contained"
          color="secondary"
        >
          <Typography component="span" style={{ ...fontSize, ...styles.typographyAsContainer }}>
            <Adjust />
            Смяна на технология
          </Typography>
        </Button>
      </Box>
      <Dialog open={openAmendForm}>
        {openAmendForm && <AmendForm setOpenAmendForm={setOpenAmendForm} ticketId={ticketId} />}
      </Dialog>
    </>
  );
};

export default Amend;
